import { OPS_SURFACE_LABELS } from './opsSurfaceLabels';

export type QuickActionCategory = 'booking' | 'planning' | 'ai' | 'finance' | 'crm' | 'other';

export interface QuickActionDefinition {
  id: string;
  label: string;
  path: string;
  category: QuickActionCategory;
}

export const QUICK_ACTIONS_REGISTRY = {
  'ops-inbox': {
    id: 'ops-inbox',
    label: 'Ops Inbox',
    path: '/ops/inbox',
    category: 'booking',
  },
  'ops-supervisor': {
    id: 'ops-supervisor',
    label: OPS_SURFACE_LABELS['team-dispatch'].label,
    path: '/ops/dispatch',
    category: 'other',
  },
  'ops-control-tower': {
    id: 'ops-control-tower',
    label: 'Ops Control Tower',
    path: '/ops/control-tower',
    category: 'other',
  },
  'ops-stale-work': {
    id: 'ops-stale-work',
    label: 'Stale Work Review',
    path: '/ops/stale-work',
    category: 'other',
  },
  aoc: {
    id: 'aoc',
    label: 'Aviation Ops Center',
    path: '/aoc',
    category: 'other',
  },
  'ops-handbook': {
    id: 'ops-handbook',
    label: 'AOC + Ops Handbook',
    path: '/ops/handbook',
    category: 'other',
  },
  'paste-and-book': {
    id: 'paste-and-book',
    label: 'Quick Book',
    path: '/quick-book',
    category: 'booking',
  },
  pnr: {
    id: 'pnr',
    label: 'Booking Manager',
    path: '/pnr',
    category: 'booking',
  },
  customers: {
    id: 'customers',
    label: 'Customer Database',
    path: '/customers',
    category: 'crm',
  },
  invoices: {
    id: 'invoices',
    label: 'Invoices',
    path: '/invoices',
    category: 'finance',
  },
  rtw: {
    id: 'rtw',
    label: 'RTW Planner',
    path: '/rtw',
    category: 'planning',
  },
  'ai-assistant': {
    id: 'ai-assistant',
    label: 'AI Assistant',
    path: '/ai-assistant',
    category: 'ai',
  },
  finance: {
    id: 'finance',
    label: 'Finance Dashboard',
    path: '/finance',
    category: 'finance',
  },
  pnt: {
    id: 'pnt',
    label: 'PNT Tracking',
    path: '/pnt',
    category: 'booking',
  },
  settings: {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    category: 'other',
  },
} as const satisfies Record<string, QuickActionDefinition>;

export type QuickActionId = keyof typeof QUICK_ACTIONS_REGISTRY;

export const DEFAULT_QUICK_ACTION_IDS: QuickActionId[] = ['ops-inbox', 'paste-and-book', 'pnr', 'customers'];

const LEGACY_QUICK_ACTION_ID_MIGRATION: Record<string, QuickActionId> = {
  'quick-book': 'paste-and-book',
  'new-pnr': 'pnr',
  'add-customer': 'customers',
  'create-invoice': 'invoices',
  'new-rtw': 'rtw',
  'ask-ai': 'ai-assistant',
  'pnt-tracking': 'pnt',
  'finance-dashboard': 'finance',
  'operations-inbox': 'ops-inbox',
};

export function resolveLegacyQuickActionId(actionId: string): QuickActionId | null {
  if (actionId in QUICK_ACTIONS_REGISTRY) {
    return actionId as QuickActionId;
  }

  return LEGACY_QUICK_ACTION_ID_MIGRATION[actionId] ?? null;
}

export function normalizeQuickActionIds(actionIds: string[], source = 'unknown'): QuickActionId[] {
  const resolved: QuickActionId[] = [];

  for (const rawId of actionIds) {
    const migrated = resolveLegacyQuickActionId(rawId);

    if (!migrated) {
      console.warn(`[quick-actions] Unknown action ID "${rawId}" loaded from ${source}; ignoring.`);
      continue;
    }

    if (!resolved.includes(migrated)) {
      resolved.push(migrated);
    }
  }

  return resolved;
}

export function getQuickActionById(actionId: string): QuickActionDefinition | undefined {
  const normalized = resolveLegacyQuickActionId(actionId);
  return normalized ? QUICK_ACTIONS_REGISTRY[normalized] : undefined;
}

export function getQuickActionPath(actionId: string): string | null {
  return getQuickActionById(actionId)?.path ?? null;
}

export const QUICK_ACTIONS_LIST: QuickActionDefinition[] = Object.values(QUICK_ACTIONS_REGISTRY);
